/**
 * DOM ↔ spec element picker for the devtools panel.
 *
 * Two affordances:
 * - `startPicker(onPick)` — enters click-to-select mode. The next
 *   element with a `data-dash-key` attribute the user clicks fires
 *   the callback with that key. The hover gets a translucent outline
 *   while picking is active.
 * - `setHoverHighlight(key)` — the panel calls this to highlight a
 *   key from the spec tab; the matching DOM node gets the same
 *   outline. Pass `null` to clear.
 *
 * Implementation is browser-only (no React) so the same code can be
 * called from the panel hook OR from a future picker tab. SSR-safe:
 * functions noop when `window` is undefined.
 */

export const DEVTOOLS_KEY_ATTR = "data-dash-key"

const PICKER_OUTLINE_CLASS = "__dash-picker-outline"
const PICKER_OUTLINE_STYLE = `
.${PICKER_OUTLINE_CLASS} {
  outline: 2px solid rgb(99 102 241) !important; /* indigo-500 */
  outline-offset: 2px !important;
  cursor: crosshair !important;
}
`

let styleInjected = false

function ensureStyleInjected(): void {
  if (styleInjected || typeof document === "undefined") return
  const style = document.createElement("style")
  style.setAttribute("data-dash-picker-style", "")
  style.textContent = PICKER_OUTLINE_STYLE
  document.head.appendChild(style)
  styleInjected = true
}

/**
 * Find a tile by spec key. Returns the first matching element or null.
 */
export function findElementByKey(key: string): HTMLElement | null {
  if (typeof document === "undefined") return null
  return document.querySelector<HTMLElement>(`[${DEVTOOLS_KEY_ATTR}=${JSON.stringify(key)}]`)
}

/**
 * Set or clear the hover outline on the element matching `key`.
 * Idempotent — calling with the same key repeatedly is a no-op,
 * calling with a different key clears the prior outline first.
 */
let lastHighlighted: HTMLElement | null = null
export function setHoverHighlight(key: string | null): void {
  if (typeof document === "undefined") return
  ensureStyleInjected()
  if (lastHighlighted) {
    lastHighlighted.classList.remove(PICKER_OUTLINE_CLASS)
    lastHighlighted = null
  }
  if (!key) return
  const next = findElementByKey(key)
  if (!next) return
  next.classList.add(PICKER_OUTLINE_CLASS)
  lastHighlighted = next
}

export interface PickerSession {
  /** Tear down picker mode without firing onPick. */
  stop(): void
}

export interface PickerOptions {
  /** Called when the user clicks an element with a `data-dash-key`. */
  onPick: (key: string) => void
  /** Optional cancel callback — fired on Escape or outside-click. */
  onCancel?: () => void
}

/**
 * Enter picker mode. Returns a session handle the panel can call
 * `.stop()` on to tear down — typically wired to a "Cancel" button or
 * an Escape keypress (handled internally too).
 */
export function startPicker(options: PickerOptions): PickerSession {
  if (typeof document === "undefined") {
    return { stop: () => {} }
  }
  ensureStyleInjected()

  let activeOutline: HTMLElement | null = null

  const findKeyedAncestor = (target: EventTarget | null): HTMLElement | null => {
    if (!(target instanceof Element)) return null
    return target.closest<HTMLElement>(`[${DEVTOOLS_KEY_ATTR}]`)
  }

  const onMove = (e: MouseEvent) => {
    const next = findKeyedAncestor(e.target)
    if (next === activeOutline) return
    if (activeOutline) activeOutline.classList.remove(PICKER_OUTLINE_CLASS)
    activeOutline = next
    if (activeOutline) activeOutline.classList.add(PICKER_OUTLINE_CLASS)
  }

  const onClick = (e: MouseEvent) => {
    const target = findKeyedAncestor(e.target)
    if (!target) return
    e.preventDefault()
    e.stopPropagation()
    const key = target.getAttribute(DEVTOOLS_KEY_ATTR)
    if (key) options.onPick(key)
    cleanup()
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      options.onCancel?.()
      cleanup()
    }
  }

  const cleanup = () => {
    document.removeEventListener("mousemove", onMove, true)
    document.removeEventListener("click", onClick, true)
    document.removeEventListener("keydown", onKey, true)
    if (activeOutline) {
      activeOutline.classList.remove(PICKER_OUTLINE_CLASS)
      activeOutline = null
    }
    document.body.style.cursor = ""
  }

  // Capture-phase listeners so the picker beats normal click handlers
  // (otherwise clicking a tile would just toggle its selection).
  document.addEventListener("mousemove", onMove, true)
  document.addEventListener("click", onClick, true)
  document.addEventListener("keydown", onKey, true)
  document.body.style.cursor = "crosshair"

  return { stop: cleanup }
}
